import { formatCurrency } from "@/lib/format";

/**
 * A taxa de entrega e as formas de pagamento do estabelecimento, só para
 * exibir no detalhe do vendedor. Quem grava é o app do vendedor, e a conta da
 * taxa é a de `domain/utils/deliveryPricing.ts` dos dois aplicativos (esta é
 * uma cópia reduzida, que só descreve a configuração).
 */

export type DeliveryPricingMode = "fixed" | "distance" | "ranges";

export interface DeliveryPricing {
  mode: DeliveryPricingMode;
  maxDistanceKm?: number | null;
  distance?: { minKm: number; minFee: number; maxKm: number; maxFee: number } | null;
  ranges?: { upToKm: number; fee: number }[] | null;
}

export const DELIVERY_PRICING_MODE_LABEL: Record<DeliveryPricingMode, string> = {
  fixed: "Fixa",
  distance: "Por distância",
  ranges: "Por faixas",
};

const finite = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const km = (value: number) =>
  `${Number.isInteger(value) ? value : value.toFixed(1).replace(".", ",")} km`;

/**
 * "Fixa, R$ 5,00", "Por distância: R$ 5,00 até 1 km, R$ 15,00 a partir de
 * 20 km" ou "Por faixas: até 3 km, R$ 4,00; até 6 km, R$ 7,00", com a
 * distância máxima de entrega no fim. Sem o modo (estabelecimento antigo),
 * vale a taxa fixa.
 */
export function deliveryPricingDescription(seller: {
  deliveryFee?: number;
  deliveryPricing?: DeliveryPricing | null;
}): string | null {
  const pricing = seller.deliveryPricing;
  const parts: string[] = [];
  if (pricing?.mode === "distance" && pricing.distance) {
    const { minKm, minFee, maxKm, maxFee } = pricing.distance;
    parts.push(
      `Por distância: ${formatCurrency(minFee)} até ${km(minKm)}, ${formatCurrency(maxFee)} a partir de ${km(maxKm)}`
    );
  } else if (pricing?.mode === "ranges" && pricing.ranges?.length) {
    parts.push(
      `Por faixas: ${pricing.ranges
        .map((range) => `até ${km(range.upToKm)}, ${formatCurrency(range.fee)}`)
        .join("; ")}`
    );
  } else if (finite(seller.deliveryFee)) {
    parts.push(`Fixa, ${formatCurrency(seller.deliveryFee)}`);
  }
  if (pricing && finite(pricing.maxDistanceKm) && pricing.maxDistanceKm > 0) {
    parts.push(`entrega até ${km(pricing.maxDistanceKm)}`);
  }
  return parts.length > 0 ? parts.join(". ") : null;
}

export const PAYMENT_METHOD_LABEL: Record<string, string> = {
  credit_card: "Cartão de crédito",
  debit_card: "Cartão de débito",
  pix: "PIX",
  cash: "Dinheiro",
};

/** O nome da forma de pagamento gravada no pedido. */
export const paymentMethodLabel = (value?: string | null): string =>
  (value && PAYMENT_METHOD_LABEL[value]) || value || "";

/**
 * As formas que o estabelecimento aceita. Sem o campo, o cliente vê as
 * quatro, e o app do vendedor não deixa o estabelecimento abrir até ele
 * escolher.
 */
export function paymentMethodsDescription(seller: { paymentMethods?: string[] | null }): string {
  const methods = (seller.paymentMethods ?? []).filter((value) => value in PAYMENT_METHOD_LABEL);
  if (methods.length === 0) return "Não escolhidas (o cliente vê todas)";
  return methods.map((value) => PAYMENT_METHOD_LABEL[value]).join(", ");
}
